import { Fragment, useEffect, useRef, useState } from "react";
import api from "../../api/client";

// ─── Third-party detection ────────────────────────────────────────────────────

// Compiled provider patterns — populated once from the API on first use.
// The module-level ref means concurrent HarViewer mounts share the same fetch.
let _thirdPartyPromise = null;

function loadThirdParty() {
  if (!_thirdPartyPromise) {
    _thirdPartyPromise = api
      .get("/third-party-providers")
      .then((res) =>
        (res.data?.data ?? []).map(({ name, category, pattern }) => ({
          name,
          category,
          pattern: new RegExp(pattern, "i"),
        }))
      )
      .catch(() => []);
  }
  return _thirdPartyPromise;
}

function classifyThirdParty(url, providers) {
  for (const { name, category, pattern } of providers) {
    if (pattern.test(url)) return { name, category };
  }
  return null;
}

// ─── Timing segment definitions (rendered in order) ──────────────────────────

const SEGMENTS = [
  { key: "blocked",  label: "Blocked",   color: "#d1d5db" },
  { key: "dns",      label: "DNS",        color: "#7c3aed" },
  { key: "tcp",      label: "TCP",        color: "#ea580c" },
  { key: "ssl",      label: "SSL",        color: "#d97706" },
  { key: "ttfb",     label: "TTFB",       color: "#0D9488" },
  { key: "download", label: "Download",   color: "#16a34a" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function clamp(v) {
  return v == null || v < 0 ? 0 : v;
}

function formatBytes(n) {
  if (n == null || n < 0) return "—";
  if (n === 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / 1048576).toFixed(2)} MB`;
}

function formatMs(ms) {
  if (ms == null || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function resourceType(entry) {
  const mime = (entry.response?.content?.mimeType ?? "").split(";")[0].trim();
  if (mime.includes("html"))                                    return "Doc";
  if (mime.includes("css"))                                     return "CSS";
  if (mime.includes("javascript") || mime.includes("ecmascript")) return "JS";
  if (mime.startsWith("image/"))                                return "Img";
  if (mime.includes("font"))                                    return "Font";
  if (mime.includes("json"))                                    return "JSON";
  if (mime.includes("xml"))                                     return "XML";
  if (mime.includes("wasm"))                                    return "WASM";
  return "Other";
}

function buildSegments(t) {
  const ssl = clamp(t.ssl);
  return {
    blocked:  clamp(t.blocked),
    dns:      clamp(t.dns),
    tcp:      Math.max(0, clamp(t.connect) - ssl),
    ssl,
    ttfb:     clamp(t.send) + clamp(t.wait),
    download: clamp(t.receive),
  };
}

function parseHar(har, providers = []) {
  const log = har?.log ?? {};
  const page = (log.pages ?? [])[0] ?? null;

  const rawEntries = log.entries ?? [];
  const firstTs = rawEntries[0]?.startedDateTime;
  const pageStart = page
    ? new Date(page.startedDateTime).getTime()
    : firstTs
      ? new Date(firstTs).getTime()
      : 0;

  const entries = rawEntries.map((e) => {
    const startOffset = Math.max(0, new Date(e.startedDateTime).getTime() - pageStart);
    const segments = buildSegments(e.timings ?? {});
    const contentSize = e.response?.content?.size ?? -1;
    const bodySize = e.response?.bodySize ?? -1;
    const headersSize = e.response?.headersSize ?? 0;
    const size = contentSize > 0
      ? contentSize
      : bodySize > 0
        ? bodySize + headersSize
        : -1;
    const url = e.request?.url ?? "";
    let urlObj = null;
    try { urlObj = new URL(url); } catch { /* invalid URL */ }
    const displayUrl = urlObj
      ? urlObj.hostname + (urlObj.pathname !== "/" ? urlObj.pathname : "")
      : url;

    const tp = classifyThirdParty(url, providers);
    return {
      ...e,
      _startOffset: startOffset,
      _segments: segments,
      _size: size,
      _type: resourceType(e),
      _thirdParty: tp?.name ?? null,
      _thirdPartyCategory: tp?.category ?? null,
      _displayUrl: displayUrl,
      _url: url,
      _synthetic: e._synthetic ?? false,
      _phase: e._phase ?? null,
    };
  });

  const dcl = page?.pageTimings?.onContentLoad ?? -1;
  const load = page?.pageTimings?.onLoad ?? -1;
  const maxEnd = entries.reduce((m, e) => Math.max(m, e._startOffset + (e.time ?? 0)), 0);
  const totalDuration = load > 0 ? load : maxEnd || 1;
  const totalSize = entries.reduce((s, e) => s + (e._size > 0 ? e._size : 0), 0);

  return { entries, totalDuration, totalSize, dcl, load };
}

// ─── Badges ───────────────────────────────────────────────────────────────────

const METHOD_CLS = {
  GET:    "text-green-700 bg-green-50",
  POST:   "text-teal-700 bg-teal-50",
  PUT:    "text-orange-700 bg-orange-50",
  PATCH:  "text-amber-700 bg-amber-50",
  DELETE: "text-red-700 bg-red-50",
  HEAD:   "text-purple-700 bg-purple-50",
};

function MethodBadge({ method = "GET" }) {
  return (
    <span className={[
      "inline-block rounded px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none",
      METHOD_CLS[method] ?? "text-gray-600 bg-gray-100",
    ].join(" ")}>
      {method}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls =
    status >= 500 ? "text-red-700 bg-red-50" :
    status >= 400 ? "text-orange-700 bg-orange-50" :
    status >= 300 ? "text-sky-700 bg-sky-50" :
    status >= 200 ? "text-green-700 bg-green-50" :
                    "text-gray-600 bg-gray-100";
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none ${cls}`}>
      {status || "—"}
    </span>
  );
}

const TYPE_CLS = {
  Doc:   "text-indigo-700 bg-indigo-50",
  CSS:   "text-sky-700 bg-sky-50",
  JS:    "text-yellow-700 bg-yellow-50",
  Img:   "text-pink-700 bg-pink-50",
  Font:  "text-purple-700 bg-purple-50",
  JSON:  "text-teal-700 bg-teal-50",
  XML:   "text-orange-700 bg-orange-50",
  WASM:  "text-rose-700 bg-rose-50",
};

function TypeBadge({ type }) {
  return (
    <span className={[
      "inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-none",
      TYPE_CLS[type] ?? "text-gray-500 bg-gray-100",
    ].join(" ")}>
      {type}
    </span>
  );
}

const CATEGORY_CLS = {
  analytics:    "text-teal-700 bg-teal-50",
  advertising:  "text-red-700 bg-red-50",
  marketing:    "text-pink-700 bg-pink-50",
  support:      "text-teal-700 bg-teal-50",
  social:       "text-violet-700 bg-violet-50",
  payments:     "text-emerald-700 bg-emerald-50",
  reviews:      "text-yellow-700 bg-yellow-50",
  ecommerce:    "text-indigo-700 bg-indigo-50",
  cdn:          "text-cyan-700 bg-cyan-50",
  monitoring:   "text-orange-700 bg-orange-50",
  video:        "text-rose-700 bg-rose-50",
  optimization: "text-lime-700 bg-lime-50",
};

function CategoryBadge({ category }) {
  if (!category) return null;
  return (
    <span className={[
      "inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-none",
      CATEGORY_CLS[category] ?? "text-gray-500 bg-gray-100",
    ].join(" ")}>
      {category}
    </span>
  );
}

// ─── Waterfall ────────────────────────────────────────────────────────────────

function pct(v, total) {
  return `${((v / total) * 100).toFixed(4)}%`;
}

/**
 * Horizontal waterfall bar for a single entry.
 * Position and width are expressed as percentages of totalDuration.
 * DCL and Load markers are drawn as semi-transparent vertical lines.
 */
function WaterfallBar({ entry, totalDuration, dcl, load }) {
  const segs = entry._segments;
  const segTotal = Object.values(segs).reduce((s, v) => s + v, 0) || entry.time || 1;

  return (
    <div className="relative h-4 w-full">
      {/* DCL marker */}
      {dcl > 0 && dcl <= totalDuration && (
        <div
          className="absolute inset-y-0 w-px bg-teal-400/60 z-10 pointer-events-none"
          style={{ left: pct(dcl, totalDuration) }}
        />
      )}
      {/* Load marker */}
      {load > 0 && load <= totalDuration && (
        <div
          className="absolute inset-y-0 w-px bg-red-400/60 z-10 pointer-events-none"
          style={{ left: pct(load, totalDuration) }}
        />
      )}
      {/* Timing bar */}
      <div
        className="absolute inset-y-0"
        style={{ left: pct(entry._startOffset, totalDuration), width: pct(entry.time, totalDuration) }}
      >
        <div className="flex h-full overflow-hidden rounded-sm">
          {SEGMENTS.map(({ key, color }) => {
            const val = segs[key] ?? 0;
            if (!val) return null;
            const w = (val / segTotal) * 100;
            return (
              <div
                key={key}
                className="h-full flex-none"
                style={{ width: `${w}%`, backgroundColor: color }}
                title={`${key}: ${formatMs(val)}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Column header for the waterfall: tick marks at even intervals,
 * plus DCL (blue) and Load (red) marker lines.
 */
function WaterfallHeader({ totalDuration, dcl, load }) {
  const TICKS = 4;
  return (
    <div className="relative h-6 w-full select-none">
      {Array.from({ length: TICKS + 1 }, (_, i) => {
        const frac = i / TICKS;
        return (
          <div
            key={i}
            className="absolute top-0 -translate-x-1/2"
            style={{ left: `${frac * 100}%` }}
          >
            {i > 0 && (
              <span className="font-mono text-[9px] text-gray-400">
                {formatMs(frac * totalDuration)}
              </span>
            )}
          </div>
        );
      })}
      {dcl > 0 && dcl <= totalDuration && (
        <div
          className="absolute inset-y-0 w-px bg-teal-400"
          style={{ left: pct(dcl, totalDuration) }}
          title={`DOMContentLoaded: ${formatMs(dcl)}`}
        />
      )}
      {load > 0 && load <= totalDuration && (
        <div
          className="absolute inset-y-0 w-px bg-red-400"
          style={{ left: pct(load, totalDuration) }}
          title={`Load: ${formatMs(load)}`}
        />
      )}
    </div>
  );
}

// ─── Expanded panel ───────────────────────────────────────────────────────────

function HeadersTable({ headers }) {
  if (!headers?.length) {
    return <p className="py-2 text-xs text-gray-400">None</p>;
  }
  return (
    <table className="w-full text-xs">
      <tbody>
        {headers.map((h, i) => (
          <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : ""}>
            <td className="w-48 break-all py-1 pl-2 pr-4 align-top font-mono font-medium text-gray-500">
              {h.name}
            </td>
            <td className="break-all py-1 pr-2 align-top font-mono text-gray-800">
              {h.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TimingBreakdown({ entry }) {
  const segs = entry._segments;
  const segTotal = Object.values(segs).reduce((s, v) => s + v, 0);
  return (
    <div className="space-y-1.5 py-1">
      {SEGMENTS.map(({ key, label, color }) => {
        const val = segs[key] ?? 0;
        if (!val) return null;
        const barW = segTotal > 0 ? (val / segTotal) * 100 : 0;
        return (
          <div key={key} className="flex items-center gap-3">
            <span className="h-2.5 w-2.5 flex-none rounded-sm" style={{ backgroundColor: color }} />
            <span className="w-20 text-xs text-gray-500">{label}</span>
            <div className="flex flex-1 items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full" style={{ width: `${barW}%`, backgroundColor: color }} />
              </div>
              <span className="w-20 text-right font-mono text-xs text-gray-700">{formatMs(val)}</span>
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-3 border-t border-gray-100 pt-2">
        <span className="h-2.5 w-2.5 flex-none" />
        <span className="w-20 text-xs font-semibold text-gray-700">Total</span>
        <div className="flex flex-1 justify-end">
          <span className="font-mono text-xs font-semibold text-gray-900">{formatMs(entry.time)}</span>
        </div>
      </div>
    </div>
  );
}

const EXPANDED_TABS = [
  { id: "req",    label: "Request Headers" },
  { id: "res",    label: "Response Headers" },
  { id: "timing", label: "Timing" },
];

function ExpandedPanel({ entry }) {
  const [tab, setTab] = useState("req");
  return (
    <div className="border-b border-gray-100 bg-slate-50 px-5 pb-4 pt-3">
      {/* Full URL */}
      <p className="mb-3 break-all font-mono text-xs text-gray-500">{entry._url}</p>

      {/* Tabs */}
      <div className="mb-3 flex gap-1 border-b border-gray-200">
        {EXPANDED_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={[
              "-mb-px border-b-2 px-3 py-1.5 text-xs font-medium transition-colors",
              tab === t.id
                ? "border-teal-600 text-teal-600"
                : "border-transparent text-gray-500 hover:text-gray-700",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "req"    && <HeadersTable headers={entry.request?.headers} />}
      {tab === "res"    && <HeadersTable headers={entry.response?.headers} />}
      {tab === "timing" && <TimingBreakdown entry={entry} />}
    </div>
  );
}

// ─── Summary bar ──────────────────────────────────────────────────────────────

function SummaryBar({ entries, totalSize, totalDuration, dcl, load }) {
  const stats = [
    { label: "Requests",         value: entries.length,                        mono: false },
    { label: "Transferred",      value: formatBytes(totalSize),                 mono: true  },
    { label: "Finish",           value: formatMs(load > 0 ? load : totalDuration), mono: true },
    { label: "DOMContentLoaded", value: dcl > 0 ? formatMs(dcl) : "—",         mono: true  },
  ];
  return (
    <div className="mb-4 flex flex-wrap items-end gap-x-8 gap-y-3 border-b border-gray-100 pb-4">
      {stats.map(({ label, value, mono }) => (
        <div key={label}>
          <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
          <p className={["mt-0.5 text-xl font-bold text-gray-900", mono ? "font-mono" : ""].join(" ")}>
            {value}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 border-t border-gray-100 pt-3">
      {SEGMENTS.map(({ label, color }) => (
        <span key={label} className="flex items-center gap-1.5 text-[11px] text-gray-500">
          <span className="h-2.5 w-2.5 flex-none rounded-sm" style={{ backgroundColor: color }} />
          {label}
        </span>
      ))}
      <span className="ml-2 flex items-center gap-1.5 text-[11px] text-gray-500">
        <span className="inline-block h-3 w-px bg-teal-400" />
        DCL
      </span>
      <span className="flex items-center gap-1.5 text-[11px] text-gray-500">
        <span className="inline-block h-3 w-px bg-red-400" />
        Load
      </span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function HarViewer({ har }) {
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    loadThirdParty().then(setProviders);
  }, []);

  const { entries, totalDuration, dcl, load } = parseHar(har, providers);
  const searchTerm = search.trim().toLowerCase();
  const filteredEntries = searchTerm
    ? entries.filter((entry) => {
        const thirdParty = entry._thirdParty?.toLowerCase() ?? "";
        return (
          entry._url.toLowerCase().includes(searchTerm)
          || entry._displayUrl.toLowerCase().includes(searchTerm)
          || thirdParty.includes(searchTerm)
        );
      })
    : entries;
  const filteredSize = filteredEntries.reduce((sum, entry) => sum + (entry._size > 0 ? entry._size : 0), 0);

  if (!entries.length) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-10 text-center shadow-sm">
        <p className="text-sm text-gray-400">No HAR entries to display.</p>
      </div>
    );
  }

  function toggle(i) {
    setExpanded((prev) => (prev === i ? null : i));
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="px-5 py-4">
        <SummaryBar
          entries={filteredEntries}
          totalSize={filteredSize}
          totalDuration={totalDuration}
          dcl={dcl}
          load={load}
        />

        <div className="mb-4 flex flex-col gap-2 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <label htmlFor="har-search" className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-gray-400">
              Search requests
            </label>
            <input
              id="har-search"
              type="text"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setExpanded(null);
              }}
              placeholder="Find by URL or vendor"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-teal-600 sm:w-80"
            />
          </div>
          <p className="text-xs text-gray-400">
            {searchTerm
              ? `Showing ${filteredEntries.length} of ${entries.length} requests`
              : `${entries.length} requests`}
          </p>
        </div>

        {!filteredEntries.length && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
            <p className="text-sm text-gray-500">No requests match your search.</p>
          </div>
        )}

        {filteredEntries.length > 0 && (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="w-14  py-1.5 pr-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  Method
                </th>
                <th className="w-10  py-1.5 pr-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  Status
                </th>
                <th className="w-12  py-1.5 pr-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  Type
                </th>
                <th className="       py-1.5 pr-4 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  URL
                </th>
                <th className="w-20  py-1.5 pr-4 text-right text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  Size
                </th>
                {/* Waterfall header contains tick marks and DCL/Load markers */}
                <th className="w-72  py-0    pr-2 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                    Waterfall
                  </div>
                  <WaterfallHeader totalDuration={totalDuration} dcl={dcl} load={load} />
                </th>
                <th className="w-20  py-1.5 text-right text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  Time
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredEntries.map((entry, i) => (
                <Fragment key={i}>
                  <tr
                    className={[
                      "cursor-pointer border-b border-gray-50 transition-colors hover:bg-teal-50/50",
                      expanded === i ? "bg-teal-50/50" : "",
                      entry._synthetic ? "opacity-60 italic" : "",
                    ].join(" ")}
                    onClick={() => toggle(i)}
                    title={entry._synthetic ? `Captured by event listener (not in HAR)${entry._phase ? ` — phase: ${entry._phase}` : ""}` : undefined}
                  >
                    <td className="py-2 pr-2">
                      <MethodBadge method={entry.request?.method} />
                    </td>

                    <td className="py-2 pr-2">
                      <StatusBadge status={entry.response?.status} />
                    </td>

                    <td className="py-2 pr-2">
                      <TypeBadge type={entry._type} />
                    </td>

                    <td className="py-2 pr-4">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className="max-w-xs truncate font-mono text-xs text-gray-700"
                          title={entry._url}
                        >
                          {entry._displayUrl}
                        </span>
                        {entry._thirdPartyCategory && (
                          <CategoryBadge category={entry._thirdPartyCategory} />
                        )}
                        {entry._thirdParty && (
                          <span className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-amber-700">
                            {entry._thirdParty}
                          </span>
                        )}
                        {entry._phase && (
                          <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-gray-500">
                            {entry._phase}
                          </span>
                        )}
                      </div>
                    </td>

                    <td className="py-2 pr-4 text-right font-mono text-xs text-gray-500">
                      {formatBytes(entry._size)}
                    </td>

                    <td className="py-2 pr-2">
                      <WaterfallBar
                        entry={entry}
                        totalDuration={totalDuration}
                        dcl={dcl}
                        load={load}
                      />
                    </td>

                    <td className="py-2 text-right font-mono text-xs text-gray-700">
                      {formatMs(entry.time)}
                    </td>
                  </tr>

                  {expanded === i && (
                    <tr>
                      <td colSpan={7} className="p-0">
                        <ExpandedPanel entry={entry} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}

        <Legend />
      </div>
    </div>
  );
}
